import type {
  ClinicEnrollmentRow,
  ClinicEnrollmentTask,
  ClinicEnrollmentTaskLink,
  EnrollmentDisplayStatus,
  EnrollmentSupport,
  PayerClinicLine,
  PayerEnrollmentGroup,
} from '../../services/clinicsApi';

export type PayerTransaction = 'claims' | 'eligibility' | 'remittance';

/** Claims first, then eligibility, then remittance — the order the team chases. */
export const PAYER_TRANSACTIONS: PayerTransaction[] = [
  'claims',
  'eligibility',
  'remittance',
];

/** The transactions the filter lets through, in order; no filter means all. */
export const transactionsInView = (
  selected: PayerTransaction[]
): PayerTransaction[] =>
  selected.length === 0
    ? PAYER_TRANSACTIONS
    : PAYER_TRANSACTIONS.filter(transaction => selected.includes(transaction));

export const PAYER_TRANSACTION_LABELS: Record<PayerTransaction, string> = {
  claims: 'Claims',
  eligibility: 'Eligibility',
  remittance: 'Remittance',
};

/** Stedi's own tooltip wording from its payer network table. */
export const SUPPORT_LABELS: Record<EnrollmentSupport, string> = {
  enrollment_not_required: 'Supported, enrollment not required',
  enrollment_required: 'Supported, enrollment required',
  not_supported: 'Not supported',
  unknown: 'Not known',
};

const DONE: EnrollmentDisplayStatus[] = ['live', 'not_required'];
const NEEDS_SOMEBODY: EnrollmentDisplayStatus[] = [
  'action_required',
  'rejected',
];

/** The clinic lines the clinic filter lets through; no filter means all. */
export const linesInView = (
  payer: PayerEnrollmentGroup,
  clinicIds: string[]
): PayerClinicLine[] =>
  (payer.clinics ?? []).filter(
    line => clinicIds.length === 0 || clinicIds.includes(line.agency_clinic_id)
  );

/** A clinic line whose insurances with the payer are all retired in WellSky. */
export const isRetiredLine = (
  line: PayerClinicLine,
  retiredInsuranceIds: Set<string>
): boolean =>
  (line.insurances ?? []).length > 0 &&
  (line.insurances ?? []).every(insurance =>
    retiredInsuranceIds.has(insurance.id)
  );

/**
 * Each payer's live clinic lines and its retired ones, as two lists. A payer
 * with both is in each, holding only that side's lines; a line with no
 * insurance (an enrollment record only) counts as live.
 */
export const splitRetired = (
  payers: PayerEnrollmentGroup[],
  retiredInsuranceIds: Set<string>
): { live: PayerEnrollmentGroup[]; retired: PayerEnrollmentGroup[] } => {
  const live: PayerEnrollmentGroup[] = [];
  const retired: PayerEnrollmentGroup[] = [];
  for (const payer of payers) {
    const lines = payer.clinics ?? [];
    const retiredLines = lines.filter(line =>
      isRetiredLine(line, retiredInsuranceIds)
    );
    if (retiredLines.length === 0) {
      live.push(payer);
      continue;
    }
    const liveLines = lines.filter(line => !retiredLines.includes(line));
    if (liveLines.length > 0) live.push({ ...payer, clinics: liveLines });
    retired.push({ ...payer, clinics: retiredLines });
  }
  return { live, retired };
};

const rowsFor = (
  lines: PayerClinicLine[],
  transaction: PayerTransaction
): ClinicEnrollmentRow[] =>
  lines
    .map(line => line[transaction])
    .filter((row): row is ClinicEnrollmentRow => Boolean(row));

export type SummaryTone =
  | 'needs_action'
  | 'waiting'
  | 'partial'
  | 'not_started'
  | 'enrolled'
  | 'none_needed';

/** Where one transaction stands across the clinics in view: its worst state. */
const transactionState = (rows: ClinicEnrollmentRow[]): SummaryTone | null => {
  const counted = rows.filter(
    row => row.status !== 'not_available' && row.status !== 'unknown'
  );
  if (counted.length === 0) return null;
  const has = (status: EnrollmentDisplayStatus) =>
    counted.some(row => row.status === status);
  if (counted.some(row => NEEDS_SOMEBODY.includes(row.status))) {
    return 'needs_action';
  }
  // A clinic nobody has started outranks one that is submitted: the agency
  // still has something to do there.
  if (has('not_started')) return has('live') ? 'partial' : 'not_started';
  if (has('waiting')) return 'waiting';
  if (counted.every(row => row.status === 'not_required')) return 'none_needed';
  if (counted.every(row => DONE.includes(row.status))) return 'enrolled';
  return has('live') ? 'partial' : 'not_started';
};

/** Where each transaction in view stands, leaving out ones with no status. */
export const transactionStates = (
  payer: PayerEnrollmentGroup,
  clinicIds: string[],
  transactions: PayerTransaction[] = PAYER_TRANSACTIONS
): { transaction: PayerTransaction; state: SummaryTone }[] => {
  const lines = linesInView(payer, clinicIds);
  return transactions
    .map(transaction => ({
      transaction,
      state: transactionState(rowsFor(lines, transaction)),
    }))
    .filter(
      (item): item is { transaction: PayerTransaction; state: SummaryTone } =>
        item.state !== null
    );
};

/** A transaction's state in a few words, for the mark's tooltip. */
export const STATE_LABELS: Record<SummaryTone, string> = {
  needs_action: 'Needs action',
  waiting: 'Submitted',
  partial: 'Not started',
  not_started: 'Not started',
  enrolled: 'Live',
  none_needed: 'No enrollment needed',
};

export type PayerStatus = Exclude<SummaryTone, 'partial'>;

/**
 * One status for the payer row, across the transactions and clinics in view:
 * somebody has to act, then anything required and not yet started, then
 * something submitted, then done. Automatic transactions are not
 * progress, so live at one clinic and not started at another is Not started —
 * whatever was started shows as Needs action or Submitted on its own.
 */
export const payerStatus = (
  payer: PayerEnrollmentGroup,
  clinicIds: string[],
  transactions: PayerTransaction[] = PAYER_TRANSACTIONS
): PayerStatus | null => {
  const states = transactionStates(payer, clinicIds, transactions).map(
    item => item.state
  );
  if (states.length === 0) return null;
  if (states.includes('needs_action')) return 'needs_action';
  if (states.some(state => state === 'not_started' || state === 'partial')) {
    return 'not_started';
  }
  if (states.includes('waiting')) return 'waiting';
  if (states.every(state => state === 'none_needed')) return 'none_needed';
  return 'enrolled';
};

/** The payer row's status in words. */
export const PAYER_STATUS_LABELS: Record<PayerStatus, string> = {
  needs_action: 'Needs action',
  waiting: 'Submitted',
  not_started: 'Not started',
  enrolled: 'Live',
  none_needed: 'No enrollment needed',
};

/** Every per-clinic status the tab count counts, across all payers. */
export const countOutstanding = (payers: PayerEnrollmentGroup[]): number =>
  payers.reduce(
    (total, payer) =>
      total +
      PAYER_TRANSACTIONS.reduce(
        (sum, transaction) =>
          sum +
          rowsFor(payer.clinics ?? [], transaction).filter(row =>
            NEEDS_SOMEBODY.includes(row.status)
          ).length,
        0
      ),
    0
  );

// Payer order: work first, then what is in hand, then what needs nothing.
// A transaction's state is ranked here, and a payer by its most urgent one.
const SUMMARY_ORDER: SummaryTone[] = [
  'needs_action',
  'not_started',
  'partial',
  'waiting',
  'enrolled',
  'none_needed',
];

/**
 * Sort position for a payer: its summary's place, then no status last. Work
 * that is only remittance sorts after the same work on claims or eligibility:
 * remittance comes last in onboarding, and switching it on too early cuts the
 * agency off from how it gets remittance today.
 */
export const summaryRank = (
  payer: PayerEnrollmentGroup,
  clinicIds: string[],
  transactions: PayerTransaction[] = PAYER_TRANSACTIONS
): number => {
  const states = transactionStates(payer, clinicIds, transactions);
  if (states.length === 0) return SUMMARY_ORDER.length * 2;
  const worst = Math.min(
    ...states.map(item => SUMMARY_ORDER.indexOf(item.state))
  );
  const leading = states.filter(
    item => SUMMARY_ORDER.indexOf(item.state) === worst
  );
  const onlyRemittance = leading.every(
    item => item.transaction === 'remittance'
  );
  return worst * 2 + (onlyRemittance ? 1 : 0);
};

/** One open enrollment step and the clinic and payer it belongs to. */
export interface TodoStep {
  row: ClinicEnrollmentRow;
  clinicName: string;
  payerKey: string;
  payerName: string;
  transaction: PayerTransaction;
  /** The next open task; null for a draft or a rejection with none. */
  task: ClinicEnrollmentTask | null;
}

/**
 * One thing the agency has to go and do. Steps that send the agency to the
 * same instruction pages are one job however many clinics and payers they
 * cover: one NGS attestation takes up to 25 NPIs, and one Zelis sign-up
 * covers every Zelis payer.
 */
export interface EnrollmentTodo {
  key: string;
  /** The shared instruction pages; empty for a step that stands alone. */
  links: ClinicEnrollmentTaskLink[];
  steps: TodoStep[];
}

/**
 * Every open step in view, with link-only steps merged by their pages.
 *
 * A step that asks for a typed value (a provider ID, bank details) or a
 * rejection stays on its own: the answer or the reason differs per clinic.
 * So does one the agency cannot tick from Roger, so it cannot block the rest.
 * Steps merge only when their instructions match too: a payer's page is often
 * linked from every step of its enrollment, and one tick completes them all.
 */
export const enrollmentTodos = (
  payers: PayerEnrollmentGroup[],
  clinicIds: string[],
  transactions: PayerTransaction[] = PAYER_TRANSACTIONS
): EnrollmentTodo[] => {
  const todos = new Map<string, EnrollmentTodo>();
  for (const payer of payers) {
    for (const line of linesInView(payer, clinicIds)) {
      for (const transaction of transactions) {
        const row = line[transaction];
        if (!row || !NEEDS_SOMEBODY.includes(row.status)) continue;
        const task = (row.tasks ?? []).find(t => !t.is_complete) ?? null;
        const links = task?.links ?? [];
        const linkOnly =
          row.status === 'action_required' &&
          links.length > 0 &&
          (task?.fields ?? []).length === 0 &&
          task?.answerable === true;
        const key = linkOnly
          ? `links:${links
              .map(link => link.url)
              .sort()
              .join(' ')}:${transaction}:${task?.instructions ?? ''}`
          : `step:${line.agency_clinic_id}:${payer.key}:${transaction}`;
        const todo = todos.get(key) ?? {
          key,
          links: linkOnly ? links : [],
          steps: [],
        };
        todo.steps.push({
          row,
          clinicName: clinicLabel(line.clinic_name),
          payerKey: payer.key,
          payerName: payer.name,
          transaction,
          task,
        });
        todos.set(key, todo);
      }
    }
  }
  // Claims and eligibility before remittance, the order onboarding needs
  // them; payer order is kept within each.
  return [...todos.values()].sort(
    (a, b) =>
      PAYER_TRANSACTIONS.indexOf(a.steps[0].transaction) -
      PAYER_TRANSACTIONS.indexOf(b.steps[0].transaction)
  );
};

/**
 * A name for an instruction page: its link text, or the page's own slug —
 * "Zelis-211685bc…" reads as "Zelis".
 */
export const linkName = (link: ClinicEnrollmentTaskLink): string => {
  if (link.label) return link.label;
  const slug = link.url.split('?')[0].split('/').pop() ?? '';
  return slug.replace(/-?[0-9a-f]{32}$/, '').replace(/-/g, ' ') || link.url;
};

/** Agencies retire an insurance by renaming it, e.g. "DNU Aetna" or "ZZ Aetna". */
export const isDoNotUseName = (name: string): boolean =>
  /^(\(dnu\)|(dnu|zz|do not use)\b)/i.test(name.trim());

/**
 * The payer as the agency knows it: the names of its own insurances that bill
 * this payer, at the clinics in view, the ones in use first. One name and a
 * count keeps a title to one line; ``all`` is for hover text. Null when the
 * agency has no insurance for the payer, such as one it does not bill yet.
 */
export const agencyTitle = (
  payer: PayerEnrollmentGroup,
  clinicIds: string[]
): { title: string; all: string[] } | null => {
  const names = [
    ...new Set(
      linesInView(payer, clinicIds).flatMap(line =>
        (line.insurances ?? []).map(insurance => insurance.name)
      )
    ),
  ].sort(
    (a, b) =>
      Number(isDoNotUseName(a)) - Number(isDoNotUseName(b)) ||
      a.localeCompare(b)
  );
  if (names.length === 0) return null;
  const more = names.length - 1;
  return {
    title: more > 0 ? `${names[0]} +${more} more` : names[0],
    all: names,
  };
};

/** A mark in words: where it stands, and why it is live only when it is. */
export const markLabel = (
  support?: EnrollmentSupport,
  state?: SummaryTone | null
): string => {
  const value = support ?? 'unknown';
  const tracked = value === 'enrollment_required' && state !== undefined;
  if (value === 'enrollment_not_required') {
    return 'Live (enrollment not required)';
  }
  if (tracked && state === 'enrolled') return 'Live (enrolled)';
  if (tracked) return state ? STATE_LABELS[state] : 'No status yet';
  return SUPPORT_LABELS[value];
};

/**
 * A clinic's name to show, never blank: a clinic saved without one would
 * otherwise leave an empty line, or a sentence that starts with a colon.
 */
export const clinicLabel = (name?: string | null): string =>
  name?.trim() || 'Unnamed clinic';

/**
 * The clinic's name as the rest of the app shows it ("Default Clinic" for a
 * Kinnser clinic with none), from the admin's clinic list when it is there.
 */
export const clinicNameFrom = (
  names: ReadonlyMap<string, string>,
  clinicId: string,
  name?: string | null
): string => names.get(clinicId) ?? clinicLabel(name);

/** `lines` with each `clinic_name` replaced by {@link clinicNameFrom}. */
export const nameClinicLines = <
  T extends { agency_clinic_id: string; clinic_name?: string | null },
>(
  lines: T[] | undefined,
  names: ReadonlyMap<string, string>
): T[] | undefined =>
  lines?.map(line => ({
    ...line,
    clinic_name: clinicNameFrom(names, line.agency_clinic_id, line.clinic_name),
  }));

/** `payers` with every clinic line named by {@link clinicNameFrom}. */
export const withClinicNames = (
  payers: PayerEnrollmentGroup[],
  names: ReadonlyMap<string, string>
): PayerEnrollmentGroup[] =>
  payers.map(payer => ({
    ...payer,
    clinics: nameClinicLines(payer.clinics, names),
  }));
